import {
  AdyenCheckout,
  AdyenCheckoutError,
  UIElement,
  UIElementProps,
  CoreConfiguration,
  PaymentCompletedData,
  OnChangeData,
  ICore,
  PaymentFailedData
} from "@adyen/adyen-web"
import { MutableRefObject } from "react"
import { useAccessToken } from "../auth/AuthHooks"
import { usePaymentErrorHandling } from "../checkout/review/ReviewValidation"
import { validationError } from "../checkout/validation/ValidationError"
import { ValidationException } from "../checkout/validation/ValidationException"
import { useConfig } from "../Config"
import { ErrorType } from "../error/ErrorType"
import { useStoreContext } from "../i18n/StoreHooks"
import { useSentryLogging } from "../sentry/CaptureMessage"
import { useAppDispatch } from "../State"
import logger from "../utils/logger"
import { AdyenBrowserClient } from "./AdyenBrowserClient"
import { useAdyenPayment, AdyenState } from "./AdyenState"

export type AdyenEnvironment =
  | "test"
  | "live"
  | "live-us"
  | "live-au"
  | "live-apse"
  | "live-in"

const useAdyenConfig = (): {
  clientKey: string
  environment: AdyenEnvironment
} => {
  const config = useConfig()

  return {
    clientKey: config.adyen.clientKey,
    environment: config.adyen.environment as AdyenEnvironment
  }
}

export const useAdyenBrowserClient = () => {
  const { accessToken: tokenInfo } = useAccessToken()

  return new AdyenBrowserClient(() => tokenInfo?.access_token || "")
}

export const useAdyenCheckout = () => {
  const dispatch = useAppDispatch()

  const store = useStoreContext()
  const { clientKey, environment } = useAdyenConfig()
  const { onPaymentError } = usePaymentErrorHandling()
  const sentry = useSentryLogging()
  const adyenPayment = useAdyenPayment()

  const configuration: CoreConfiguration = {
    environment, // Change to 'live' for the live environment.
    clientKey, // Public key used for client-side authentication: https://docs.adyen.com/development-resources/client-side-authentication
    analytics: {
      enabled: true // Set to false to not send analytics data to Adyen.
    },
    countryCode: store.isShoppingInEurope()
      ? "LV"
      : store.country.toUpperCase(),
    onChange(
      { data, isValid: isValidUpdate }: OnChangeData,
      element: UIElement
    ) {
      if (isValidUpdate) {
        dispatch(AdyenState.actions.setAdyenPaymentData(data))
      }
    },
    onPaymentCompleted(data: PaymentCompletedData, element?: UIElement) {
      logger.info("Adyen payment completed")
    },
    onError: (e: AdyenCheckoutError) => {
      logger.warn(e)
      sentry.captureMessage("Adyen checkout error", {
        extra: { checkoutError: e?.cause }
      })
      onPaymentError(e.cause)
    },
    onPaymentFailed(data?: PaymentFailedData, component?: UIElement) {
      sentry.captureMessage(`Adyen payment failed, ${data?.resultCode}`, {
        extra: { reason: data }
      })
      const errorMessage = {
        title: "Payment failed",
        message: "Payment provider responded with an error"
      }
      if (data) {
        const cause = data["cause"]
        if (cause as AdyenCheckoutError) {
          errorMessage.title = `Payment ${cause.errorType} error (${cause.errorCode})`
          errorMessage.message = `If this error persists, please contact us with reference: ${cause.pspReference}`
        }
      }
      const exception = new ValidationException(
        "payment",
        validationError(ErrorType.PaymentError, errorMessage)
      )
      onPaymentError(exception)
    }
  }

  return {
    createDynamicCheckout: async (
      paymentContainer: MutableRefObject<HTMLDivElement | null>,
      createFn: (checkout: ICore) => Promise<UIElement<UIElementProps>>,
      extra: CoreConfiguration = {}
    ) => {
      const options = {
        ...configuration,
        ...extra,
        session: adyenPayment?.session
      }
      const checkout: ICore = await AdyenCheckout(options)

      if (paymentContainer.current) {
        createFn(checkout)
          .then(it => it.mount(paymentContainer.current! as HTMLDivElement))
          .catch(err => logger.warn(err))
      }
    },
    onPaymentError
  }
}
